import React, { useRef, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useElementSize = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [ size, setSize ] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Defer state update to next frame
        requestAnimationFrame(() => {
          setSize({ width, height });
        });
      }
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, size };
};

type TextOverflowFallbackProps = {
  children: React.ReactNode;
  fallback: React.ReactNode;
  height?: number;
};

export const TextOverflowFallback: React.FC<TextOverflowFallbackProps> = props => {
  const { classes, cx } = useStyles();
  const { ref, size } = useElementSize();
  const { ref: contentRef, size: contentSize } = useElementSize();

  const isContentOverflowing = contentSize.width > size.width;

  return (
    <div className={classes.root} ref={ref} style={{ height: props.height ?? 24 }}>
      <div ref={contentRef} className={cx(classes.content, isContentOverflowing && classes.hidden)}>
        {props.children}
      </div>
      <div className={cx(classes.fallback, !isContentOverflowing && classes.hidden)}>
        {props.fallback}
      </div>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  content: {
    position: 'absolute',
    height: '100%',
    width: 'fit-content',
    inset: 0,
    whiteSpace: 'nowrap',
  },
  hidden: {
    visibility: 'hidden',
  },
  fallback: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    inset: 0,
    textAlign: 'left'
  },
}));